// Warm, imagery-rich metaphors surfaced on loop-back branch nodes (A, A1, B,
// C, E, F) so the user on a repeat pass sees something fresh.
//
// Each metaphor is stored as just its imagery sentence. A separate pool of
// "closer" variations is composed on top at pick-time so the overall line
// feels hand-written every time rather than stamped.

import { secureRandom } from "./secureRandom";

// ---- Imagery lines -------------------------------------------------------
const IMAGES = [
  "Like an ice cube melting in the warm sun, feel the tension go away.",
  "Like a cube of sugar placed in a swimming pool, imagine the distress dissolving into nothing.",
  "Like water off a duck's back, let the stress roll off.",
  "Like fog lifting off a quiet valley at sunrise, watch the heaviness thin until you can see clear sky again.",
  "Like a clenched fist slowly opening, feel your body unclench one finger at a time.",
  "Like steam rising off a hot mug of tea, let the tension drift upward and vanish into the air.",
  "Like a snow globe settling after being shaken, watch the swirling thoughts drift gently to the bottom.",
  "Like a storm cloud blowing past, let the feeling move through you and keep going.",
  "Like a loose thread you're gently tugging free from a sweater, let the tightness unravel little by little.",
  "Like salt dissolving in a glass of water, let the distress quietly disappear until there's nothing left to find.",
  "Like a candle flame softening as it burns, feel the edges of the feeling soften too.",
  "Like a tide pulling back from the shore, let the wave of feeling recede a little more each time.",
  "Like a kite released on a breezy day, let the worry lift off your hand and drift away.",
  "Like warm butter melting on toast, feel the tightness in your body spread thin and soak in.",
  "Like leaves floating down a slow-moving stream, watch the feelings pass by without having to chase them.",
  "Like a room slowly brightening as you open the curtains, let the heaviness lift one inch at a time.",
  "Like the pressure in a shaken soda bottle settling after you leave it still, let the fizz inside you quiet down.",
  "Like footprints in wet sand getting smoothed over by gentle waves, let the sharp edges of the feeling soften.",
  "Like a tight knot gradually loosening in your hands, let the tension undo itself strand by strand.",
  "Like a flock of birds lifting off a field and scattering into the sky, let the scattered thoughts fly away one by one.",
  "Like warm honey slowly spreading through your body, feel a soft ease move from the top of your head down to your toes.",
  "Like stepping out of a stuffy room into open air, let your lungs remember how much room they have.",
  "Like a frozen stream thawing in spring, feel the stuck places quietly start to flow again.",
  "Like a heavy winter coat sliding off your shoulders, feel the weight simply leave.",
  "Like raindrops running down a windowpane, let the tense thoughts trace their path and disappear.",
  "Like a held breath finally released, let the tightness leave your chest in one long exhale.",
  "Like snow falling in a shaft of afternoon light, let the swirling mind come to rest.",
  "Like a paused song fading out, let the looping thought trail off into quiet.",
  "Like a soft blanket draped over your shoulders, let a gentle warmth settle over any lingering worry.",
  "Like a ripple on a pond smoothing itself into stillness, let the agitation flatten into calm.",
];

// ---- Closer variations ---------------------------------------------------
// Stylistic reminders that the user is free to swap the imagery for whatever
// fits them — kept deliberately varied so the line feels fresh every pass.
const CLOSERS = [
  "Be free to use any imagery that fits your thinking in this moment!",
  "Feel free to swap in any picture that fits your mind right now.",
  "Lean on whatever mental image feels true to you right now.",
  "Trade it for any picture that fits your experience in this moment.",
  "Any image that resonates will do — go with whatever comes up.",
  "Let your mind choose whichever picture feels most natural.",
  "Whatever picture comes to mind first — that's the right one for you.",
  "Use whichever mental image speaks to you in this moment.",
  "Any imagery that fits where you are right now works — trust it.",
  "Borrow any image that fits your thinking — yours is always the right one.",
];

// ---- Non-repeating sampler -----------------------------------------------
// Rolling history guards against a repeat within the last few picks.
const RECENT_IMG_WINDOW = 5;
const RECENT_CLOSER_WINDOW = 4;
let recentImages = [];
let recentClosers = [];

const pickFromPool = (pool, recent, windowSize) => {
  const filtered = pool.filter((p) => !recent.includes(p));
  const source = filtered.length > 0 ? filtered : pool;
  const choice = source[Math.floor(secureRandom() * source.length)];
  recent.push(choice);
  if (recent.length > windowSize) recent.shift();
  return choice;
};

export const pickMetaphor = () => {
  const image = pickFromPool(IMAGES, recentImages, Math.min(RECENT_IMG_WINDOW, IMAGES.length - 1));
  const closer = pickFromPool(CLOSERS, recentClosers, Math.min(RECENT_CLOSER_WINDOW, CLOSERS.length - 1));
  return `${image} ${closer}`;
};

// Reset between interventions so a fresh session starts with the full pool.
export const resetMetaphorHistory = () => {
  recentImages = [];
  recentClosers = [];
};

export default IMAGES;
